/// Firebase initialization for the web port.
///
/// The config below is for the project's "FlatoutATC Game" web app
/// (separate from FlatoutATCReports, which handles analytics). The
/// `apiKey` is public-by-design — Firebase docs explicitly note that
/// it identifies the project to the SDK and is not a secret. Real
/// security comes from Firestore Security Rules + Auth tokens.
///
/// Stored data lives at top-level collection `webUsers/{uid}`. Keep
/// any new web-specific collections under a distinct top-level name
/// (preferably `web*`) so they're easy to scope in security rules
/// and don't collide with iOS-managed collections.

import { initializeApp } from 'firebase/app'
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check'
import {
  getAnalytics,
  isSupported as analyticsIsSupported,
  setAnalyticsCollectionEnabled,
  type Analytics,
} from 'firebase/analytics'
import { getAuth } from 'firebase/auth'
import { getDatabase } from 'firebase/database'
import { getFirestore } from 'firebase/firestore'
import { getFunctions } from 'firebase/functions'
import { hasAnalyticsConsent } from '../data/consent'

const firebaseConfig = {
  apiKey: 'AIzaSyD4dpfaAsWFmWQ_vASO2HSbCeYMHS-Fi6M',
  // Custom auth domain — branded URL in OAuth popups + email
  // verification / password-reset action links. The Web OAuth
  // client in Google Cloud Console must have
  // `https://flatoutatc.com/__/auth/handler` in its Authorized
  // redirect URIs (and `https://flatoutatc.com` in JavaScript
  // origins) for Google sign-in to work — confirmed 2026-05-06.
  // The default firebaseapp.com domain stays in Firebase Auth →
  // Authorized domains so pre-flip email links keep resolving.
  authDomain: 'flatoutatc.com',
  // Realtime Database used for presence (`/presence/{uid}`); Firestore
  // is the source of truth for everything else. URL must match the
  // database created in Console → Realtime Database. If you create
  // the database in a non-default region, update this URL accordingly.
  databaseURL: 'https://flatout-atc-default-rtdb.firebaseio.com',
  projectId: 'flatout-atc',
  storageBucket: 'flatout-atc.firebasestorage.app',
  messagingSenderId: '1030397737863',
  appId: '1:1030397737863:web:e50baf0033d48a5abf3122',
  measurementId: 'G-T2V1GBLVCL',
}

// In dev builds, opt into Firebase's debug-token flow so
// `npm run dev` (and unit tests) don't have to pass real reCAPTCHA
// challenges. The first run prints a token to the console; paste it
// into Firebase Console → App Check → Apps → web app → manage debug
// tokens. MUST be set before `initializeAppCheck`.
if (import.meta.env.DEV) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true
}

export const firebaseApp = initializeApp(firebaseConfig)

// App Check: a reCAPTCHA Enterprise assessment behind the Cloud
// Functions that enforce it (`enforceAppCheck: true`: votes, visits,
// feedback, redemption, relay and the rest). Site key is
// public-by-design (it's sent to every client) and lives next to
// firebaseConfig for symmetry. Created in Cloud Console → reCAPTCHA
// Enterprise.
//
// It starts late on purpose. Once initialized, Auth and Firestore
// attach its token too and wait for it, and the first assessment
// took about 4 s on a fresh visit (2026-11-04): the Talk to the
// Developer board showed its posts at about 7 s, 4 of them spent
// waiting on reCAPTCHA. Neither Auth nor Firestore enforces App
// Check in this project (Firebase console: UNENFORCED), so they
// gain nothing from the wait. App Check now starts at the board's
// first snapshot (`releaseAppCheck`), or after APP_CHECK_FALLBACK_MS
// on pages without one, and every callable goes through
// `../firebase/callable`, which waits for it and its token, so the
// enforcing functions still get one on every call.
const APP_CHECK_SITE_KEY = '6LcoktcsAAAAAE9c9obRxnsj1nCLkXaQcG6qWIu1'
const APP_CHECK_FALLBACK_MS = 4000

let openAppCheckGate: () => void = () => {}
const appCheckGate = new Promise<void>((resolve) => {
  openAppCheckGate = resolve
})
setTimeout(() => openAppCheckGate(), APP_CHECK_FALLBACK_MS)

/// Starts App Check now if it has not started: the board calls it once
/// its first posts are on screen.
export function releaseAppCheck(): void {
  openAppCheckGate()
}

/// App Check, initialized once the gate opens.
export const appCheckReady: Promise<AppCheck> = appCheckGate.then(() =>
  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaEnterpriseProvider(APP_CHECK_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  }),
)

export const firebaseAuth = getAuth(firebaseApp)
export const firestore = getFirestore(firebaseApp)
/// Cloud Functions handle on the same region the function was
/// deployed in (us-central1, matching iOS-owned functions).
export const firebaseFunctions = getFunctions(firebaseApp, 'us-central1')
/// Realtime Database — currently used only for presence ("playing
/// now" badge). Firestore stays the source of truth for app data.
export const firebaseDatabase = getDatabase(firebaseApp)

// ─── Firebase Analytics (GA4) ──────────────────────────────────
//
// Lazy-initialised on the first event call so the heavy gtag chunk
// doesn't load on every page view. Skipped in dev (we don't want
// `npm run dev` to pollute the production GA4 property) and when
// the user has opted out via `flatout.web.analyticsOptOut = '1'`
// in localStorage — toggle wired from Settings.
//
// The `Analytics.isSupported()` check covers older browsers + SSR
// scenarios (none today, but cheap insurance).

const ANALYTICS_OPT_OUT_KEY = 'flatout.web.analyticsOptOut'
let analyticsInstance: Analytics | null = null
let analyticsInitPromise: Promise<void> | null = null

export function isAnalyticsAvailable(): boolean {
  if (import.meta.env.DEV) return false
  if (typeof window === 'undefined') return false
  // ePrivacy: analytics needs *positive* consent, not just absence
  // of opt-out. The consent module returns 'all' only after the
  // user clicks "Accept all" in the banner (or had previously
  // opted in via the analytics toggle). 'essential' or null both
  // gate analytics off.
  return hasAnalyticsConsent()
}

/// Returns the Analytics instance, initialising on first call.
/// Returns null in dev / opted-out / unsupported environments.
///
/// Synchronous accessor — kicks the async init on first call and
/// returns `null` until it resolves. Safe for tear-down paths
/// that just need to flip `setAnalyticsCollectionEnabled` on an
/// already-warm instance. **Do not use to fire events** — the
/// first call after page load returns `null` even with consent
/// granted, which silently drops the event. Use `awaitAnalytics`
/// for event-firing call sites.
export function analytics(): Analytics | null {
  if (!isAnalyticsAvailable()) return null
  if (analyticsInstance) return analyticsInstance
  if (!analyticsInitPromise) {
    analyticsInitPromise = analyticsIsSupported()
      .then((supported) => {
        if (!supported) return
        analyticsInstance = getAnalytics(firebaseApp)
        setAnalyticsCollectionEnabled(analyticsInstance, true)
      })
      .catch((err) => {
        console.warn('Firebase Analytics init failed', err)
      })
  }
  return analyticsInstance
}

/// Returns a Promise that resolves with the Analytics instance
/// once init completes (or `null` if unsupported / opted out).
/// Use from event-firing call sites so that the first event of a
/// session (typically the `purchase` event on the Stripe return
/// page) doesn't get dropped by the sync getter's init race.
export async function awaitAnalytics(): Promise<Analytics | null> {
  if (!isAnalyticsAvailable()) return null
  if (analyticsInstance) return analyticsInstance
  // Trigger init (returns the in-flight promise if already running).
  analytics()
  if (analyticsInitPromise) {
    await analyticsInitPromise
  }
  return analyticsInstance
}

/// Toggle the user's opt-out flag. UI binding lives in Settings.
export function setAnalyticsOptOut(optOut: boolean): void {
  try {
    if (optOut) {
      window.localStorage.setItem(ANALYTICS_OPT_OUT_KEY, '1')
      if (analyticsInstance) {
        setAnalyticsCollectionEnabled(analyticsInstance, false)
      }
    } else {
      window.localStorage.removeItem(ANALYTICS_OPT_OUT_KEY)
      if (analyticsInstance) {
        setAnalyticsCollectionEnabled(analyticsInstance, true)
      }
    }
  } catch (err) {
    console.warn('setAnalyticsOptOut failed', err)
  }
}

export function isAnalyticsOptedOut(): boolean {
  try {
    return window.localStorage.getItem(ANALYTICS_OPT_OUT_KEY) === '1'
  } catch {
    return false
  }
}
